import { TransactionCurrency } from '@contracts/payment/Currency';
import { yupResolver } from '@hookform/resolvers/yup';
import { AvailableCurrencyDropdownWithOptionalDefault } from '@src/clients/financialSettings/AvailableCurrencyDropdownWithOptionalDefault';
import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { useNuveiCreditCardForm } from '@src/clients/financialSettings/hooks/useNuveiCreditCardForm';
import { NewCreditCardFormContent } from '@src/clients/financialSettings/NewCreditCardFormContent';
import { useCreditCards } from '@src/clients/hooks/useCreditCards';
import { Button } from '@src/common/components/button/Button';
import { CancelButton } from '@src/common/components/button/CancelButton';
import { Divider } from '@src/common/components/Divider';
import { LoadingOverlay } from '@src/common/components/LoadingOverlay';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { Toggle } from '@src/common/components/Toggle';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { CreditCardSelector } from '@src/orders/components/preauthorization/CreditCardSelector';
import type { CreditCardRelatedFields } from '@src/orders/fields/CreditCardRelatedFields';
import { CreditCardField } from '@src/orders/fields/CreditCardRelatedFields';
import { MakePaymentAmountField } from '@src/orders/fields/MakePaymentFields';
import { usePayment } from '@src/orders/hooks/usePayment';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import { MakePaymentInputsSchema } from '@src/orders/schemas/MakePaymentInputsSchema';
import { useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const MakePaymentForm = ({
  clientId,
  orderId,
  onPaymentSuccess,
  onPaymentError,
  onCancel,
  blockedCurrency,
  maxBillable,
}: {
  orderId: string;
  clientId: string;
  onPaymentSuccess: () => void;
  onPaymentError: () => void;
  onCancel: () => void;
  blockedCurrency?: TransactionCurrency;
  maxBillable: number;
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [isSingleUseCard, setIsSingleUseCard] = useState(false);

  const makePaymentExistingCard = useForm({
    resolver: yupResolver(
      MakePaymentInputsSchema({
        useSingleUseCard: isSingleUseCard,
        maxAmount: maxBillable,
      }),
    ) as unknown as Resolver<CreditCardRelatedFields>,
    mode: 'onChange',
  });

  const amount = makePaymentExistingCard.watch(
    MakePaymentAmountField.fieldName,
  );
  const currency = makePaymentExistingCard.watch(
    CurrencyField.fieldName,
  ) as TransactionCurrency;
  const creditCardId = makePaymentExistingCard.watch(CreditCardField.fieldName);

  const { availableCreditCards } = useCreditCards({
    currency,
    clientId,
  });

  const { makePaymentWithNewCard, isLoading: isLoadingNewCardPayment } =
    useNuveiCreditCardForm({
      clientId,
      onError: onPaymentError,
      currency,
    });

  const onPaymentSucceeded = () => {
    const message = t('makePayment.success', { amount });
    showToast({ message, severity: ToastLevel.success });
    makePaymentExistingCard.reset();
    onPaymentSuccess();
  };

  const onPaymentFailed = (message: string) => {
    showToast({ message, severity: ToastLevel.error });
    makePaymentExistingCard.reset();
    onPaymentError();
  };

  const onPaymentFailedButCardSaved = ({
    errorCode,
    description,
  }: {
    description: string;
    errorCode: string;
  }) =>
    showToast({
      message: t('makePayment.failedButCardSaved.', {
        errorCode,
        description,
      }),
      severity: ToastLevel.error,
      closesAutomatically: false,
    });

  const { pay, isLoading } = usePayment({
    clientId,
    orderId,
    onError: onPaymentFailed,
    onSuccess: onPaymentSucceeded,
  });

  const submit = async (fields: CreditCardRelatedFields) => {
    if (!isSingleUseCard) {
      pay({ currency, creditCardId, amount });
    } else {
      void makePaymentWithNewCard({
        creditCardFields: fields,
        onSuccess: onPaymentSucceeded,
        onMakePaymentFailed: onPaymentFailed,
        onMakePaymentFailedButCardSaved: onPaymentFailedButCardSaved,
        saveCardOnFile: fields.saveCardOnFile,
        orderId,
        amount: fields.paymentAmount,
        currency: TransactionCurrency[fields.currency],
      });
    }
  };

  const cancel = () => {
    makePaymentExistingCard.reset();
    onCancel();
  };

  return (
    <>
      <Divider style={{ marginBottom: theme.spacing['9'] }} />

      {(isLoading || isLoadingNewCardPayment) && <LoadingOverlay />}
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          makePaymentExistingCard.handleSubmit(submit)(e);
        }}
      >
        <Stack gap={theme.spacing['6']}>
          <Stack direction="row" gap={theme.spacing['6']}>
            <ControllerFormNumberInput
              control={makePaymentExistingCard.control}
              fieldName={MakePaymentAmountField.fieldName}
              placeholder={t('preauthorization.enterAmount')}
            />
            <AvailableCurrencyDropdownWithOptionalDefault
              control={makePaymentExistingCard.control}
              defaultCurrency={blockedCurrency}
              setValue={makePaymentExistingCard.setValue}
              disabled={!!blockedCurrency}
            />
          </Stack>

          <CreditCardSelector
            control={makePaymentExistingCard.control}
            creditCards={availableCreditCards}
            disabled={isSingleUseCard}
          />
        </Stack>
        <Divider
          style={{
            marginTop: theme.spacing['6'],
            marginBottom: theme.spacing['6'],
          }}
        />

        <Toggle
          label={t('enterNewCreditCard')}
          onToggle={() => {
            setIsSingleUseCard(!isSingleUseCard);
            makePaymentExistingCard.resetField(CreditCardField.fieldName);
          }}
          checked={isSingleUseCard}
        />

        <div style={!isSingleUseCard ? { display: 'none' } : undefined}>
          <NewCreditCardFormContent
            register={makePaymentExistingCard.register}
            control={makePaymentExistingCard.control}
            formState={makePaymentExistingCard.formState}
            watch={makePaymentExistingCard.watch}
          />
        </div>

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={theme.spacing['3']}
        >
          <CancelButton onClick={cancel} />
          <Button
            disabled={isLoading}
            className={buttonStyle}
            variant="contained"
            type="submit"
          >
            {t('makePayment.confirmButton')}
          </Button>
        </Stack>
      </form>
    </>
  );
};

const buttonStyle = css({
  borderRadius: '100px',
});
